/**
 * Единое форматирование чисел для UI:
 * - разделитель тысяч: обычный пробел
 * - десятичный разделитель: запятая
 *
 * Важно: стандартный `Intl.NumberFormat('ru-RU')` часто возвращает неразрывные пробелы
 * (U+00A0 / U+202F) — визуально это "пробел", но при копировании/сравнении строк
 * даёт неожиданности. Поэтому мы нормализуем их в обычный пробел.
 */
export const RU_LOCALE = 'ru-RU' as const;

const NBSP_RE = /[\u00A0\u202F\u2009]/g;

/** Кэш форматтеров — повторные вызовы с теми же параметрами не создают новый объект */
const formattersCache = new Map<string, Intl.NumberFormat>();

function getFormatter(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = JSON.stringify(options);
  if (!formattersCache.has(key)) {
    formattersCache.set(key, new Intl.NumberFormat(RU_LOCALE, options));
  }
  return formattersCache.get(key)!;
}

export function normalizeNumberSpaces(value: string): string {
  return value.replace(NBSP_RE, ' ');
}

export function formatNumberRu(value: number, options?: Intl.NumberFormatOptions): string {
  const formatted = getFormatter(options ?? {}).format(value);
  return normalizeNumberSpaces(formatted);
}

export function formatFixedRu(value: number, fractionDigits: number): string {
  return formatNumberRu(value, {
    style: 'decimal',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

export function formatPercentRu(value: number, fractionDigits = 1, withSign = false): string {
  const sign = withSign && value > 0 ? '+' : '';
  return `${sign}${formatFixedRu(value, fractionDigits)}%`;
}

/**
 * Форматирование больших чисел с суффиксами (тыс/млн/млрд).
 */
export function formatLargeNumberRu(value: number, fractionDigits = 1): string {
  if (value >= 1_000_000_000) {
    return `${formatFixedRu(value / 1_000_000_000, fractionDigits)} млрд`;
  }
  if (value >= 1_000_000) {
    return `${formatFixedRu(value / 1_000_000, fractionDigits)} млн`;
  }
  if (value >= 1_000) {
    return `${formatFixedRu(value / 1_000, fractionDigits)} тыс`;
  }
  return formatFixedRu(value, 0);
}
